import React, { useState, useEffect } from 'react';
import { supabase, isConfigured, storePendingMarketingConsent, checkRateLimits, fetchClientIP, logEventWithIP } from '../services/supabase';
import { Mail, Loader2, ArrowRight, CheckCircle2, AlertCircle, Check, Zap, Package, Sparkles } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { PHOTOCLEAR_FEATURES, PHOTOCLEAR_TAGLINE } from '../constants';
import { buildDashboardProductCatalog, PHOTO_CLEAR_CREDIT_COST } from '@rehub/pricing';
import { authCallbackUrl, currentFirstTouch, signupUserData } from '../lib/attribution';

// --- HARDCODED PRODUCT CATALOG ---
const PRODUCT_CATALOG = buildDashboardProductCatalog(
  ((import.meta as any).env || {}) as Record<string, string | undefined>
);
const stripeKey = (import.meta as any).env?.VITE_STRIPE_PUBLISHABLE_KEY || process.env.VITE_STRIPE_PUBLISHABLE_KEY || '';
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;

interface EmbedPageProps {
  isEmbed?: boolean; // true when loaded in iframe
}

const EmbedPage: React.FC<EmbedPageProps> = ({ isEmbed = false }) => {
  const [mode, setMode] = useState<'login' | 'signup'>('signup');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.body.style.overflow = 'auto';
    document.body.style.height = 'auto';
    return () => {
      document.body.style.overflow = '';
      document.body.style.height = '';
    };
  }, []);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedMarketing, setAcceptedMarketing] = useState(false);
  const [website, setWebsite] = useState(''); // Honeypot for bots
  
  // Currency detection
  const [currency, setCurrency] = useState<string>('USD');
  const [billingTab, setBillingTab] = useState<'subscription' | 'topup'>('subscription');

  useEffect(() => {
    try {
      const locale = navigator.language || '';
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      const isUK = locale === 'en-GB'
        || timeZone.startsWith('Europe/London')
        || timeZone.startsWith('Europe/Belfast')
        || timeZone.startsWith('Europe/Jersey')
        || timeZone.startsWith('Europe/Guernsey')
        || timeZone.startsWith('Europe/Isle_of_Man');
      if (isUK) {
        setCurrency('GBP');
      }
    } catch {
      // Default USD
    }
  }, []);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!acceptedTerms) {
      setError("Please accept the Terms and Conditions to continue.");
      setLoading(false);
      return;
    }

    if (website.trim()) {
      setError("Something went wrong. Please try again.");
      setLoading(false);
      return;
    }

    if (!isConfigured) {
      setError("System Error: Authentication not configured.");
      setLoading(false);
      return;
    }

    try {
      const clientIP = await fetchClientIP();
      const rateLimitCheck = await checkRateLimits(email, clientIP ?? undefined);
      if (!rateLimitCheck.allowed) {
        setError(rateLimitCheck.message || 'Too many attempts. Please try again later.');
        setLoading(false);
        return;
      }

      if (mode === 'signup' && acceptedMarketing) {
        await storePendingMarketingConsent(email);
      }
      if (mode === 'signup') {
        localStorage.setItem('pending_marketing_consent', acceptedMarketing ? 'true' : 'false');
      }

      const touch = currentFirstTouch();
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: authCallbackUrl(window.location.origin, touch),
          data: { signup_app: 'photoclear', ...signupUserData(touch) },
        },
      });

      if (error) throw error;

      await logEventWithIP(
        'magic_link_requested',
        null,
        email,
        { mode, accepted_marketing: acceptedMarketing, user_agent: navigator.userAgent, source_app: 'photoclear' }
      );
      setSent(true);
    } catch (err: any) {
      if (err.message === 'Failed to fetch' || !navigator.onLine) {
        setError('Connection failed. Please check your internet.');
      } else {
        setError(err.message || 'Failed to send login link');
      }
    } finally {
      setLoading(false);
    }
  };

  const getDisplayProducts = () => {
    const list = billingTab === 'subscription' ? PRODUCT_CATALOG.SUBSCRIPTIONS : PRODUCT_CATALOG.TOPUPS;
    return list.map(product => {
      const priceMap = product.prices as any;
      const priceData = priceMap[currency] || priceMap['USD'];
      const costPerCreditData = product.costPerCredit as any;
      const costPerCreditText = costPerCreditData[currency] || costPerCreditData['USD'];
      
      return {
        ...product,
        displayAmount: priceData.amount.toFixed(2),
        priceId: priceData.id,
        costPerCredit: costPerCreditText
      };
    });
  };

  const currencySymbol = currency === 'GBP' ? '£' : '$';

  if (sent) {
    return (
      <div className={`min-h-screen bg-gradient-to-b from-slate-950 to-black flex items-center justify-center p-6 ${isEmbed ? '' : ''}`}>
        <div className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-2xl p-8 text-center shadow-2xl">
          <div className="w-16 h-16 bg-green-900/30 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 size={32} />
          </div>
          <h2 className="text-2xl font-bold text-slate-100 mb-2">Check your email</h2>
          <p className="text-slate-400 mb-6">
            We've sent a magic link to <span className="text-slate-200 font-medium">{email}</span>.
            <br />Click it to {mode === 'signup' ? 'create your account' : 'log in'}.
          </p>
          <button 
            onClick={() => setSent(false)}
            className="text-orange-400 hover:text-orange-300 text-sm font-medium"
          >
            Try a different email
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-b from-slate-950 to-black ${isEmbed ? 'p-4' : 'p-6 md:p-10'}`}>
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-orange-900/10 rounded-full blur-[150px]"></div>
        <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-orange-800/10 rounded-full blur-[150px]"></div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto">
        {/* Header */}
        {!isEmbed && (
          <div className="text-center mb-10">
            <img
              src="/Photoclear Logo HR Trans.png"
              alt="Photoclear"
              className="h-28 md:h-32 mx-auto mb-4"
            />
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              {PHOTOCLEAR_TAGLINE}
            </p>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-8 items-start">
          {/* Left: Auth Form */}
          <div className="bg-slate-900/80 backdrop-blur-xl border border-slate-800 rounded-3xl p-6 md:p-8 shadow-2xl">
            <div className="text-center mb-6">
              {isEmbed && (
                <img 
                  src="/Photoclear Logo HR Trans.png" 
                  alt="Photoclear" 
                  className="h-16 mx-auto mb-4"
                />
              )}
              <h2 className="text-2xl font-bold text-slate-100 mb-2">
                {mode === 'signup' ? 'Get Started Free' : 'Welcome Back'}
              </h2>
              <p className="text-slate-400">
                {mode === 'signup' 
                  ? 'Sign up and get 10 free credits!'
                  : 'Sign in to your account'}
              </p>
            </div>

            {/* Free Credits Banner */}
            {mode === 'signup' && (
              <div className="bg-gradient-to-r from-orange-600/20 to-amber-600/20 border border-orange-500/30 rounded-xl p-4 mb-6 flex items-center gap-3">
                <div className="p-2 bg-orange-500 rounded-full">
                  <Sparkles size={20} className="text-white" />
                </div>
                <div>
                  <p className="text-orange-200 font-semibold">10 Free Credits</p>
                  <p className="text-orange-300/70 text-sm">No credit card required</p>
                </div>
              </div>
            )}

            {/* Consent Checkboxes */}
            <div className="mb-6 space-y-3 text-left bg-slate-800/50 p-3 rounded-xl border border-slate-800">
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="relative flex items-center pt-0.5">
                  <input 
                    type="checkbox" 
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="peer h-4 w-4 shrink-0 rounded border-slate-600 bg-slate-900 appearance-none border checked:bg-orange-600 checked:border-orange-600 transition-all"
                  />
                  <Check className="absolute top-0.5 left-0.5 h-3 w-3 text-white opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity" strokeWidth={3.5} />
                </div>
                <span className="text-xs text-slate-400 group-hover:text-slate-300 transition-colors select-none leading-relaxed">
                  I accept the <a href="#" className="text-orange-400 hover:underline font-medium">Terms and Conditions</a> and <a href="#" className="text-orange-400 hover:underline font-medium">Privacy Policy</a>.
                </span>
              </label>

              {mode === 'signup' && (
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative flex items-center pt-0.5">
                    <input 
                      type="checkbox" 
                      checked={acceptedMarketing}
                      onChange={(e) => setAcceptedMarketing(e.target.checked)}
                      className="peer h-4 w-4 shrink-0 rounded border-slate-600 bg-slate-900 appearance-none border checked:bg-orange-600 checked:border-orange-600 transition-all"
                    />
                    <Check className="absolute top-0.5 left-0.5 h-3 w-3 text-white opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity" strokeWidth={3.5} />
                  </div>
                  <span className="text-xs text-slate-400 group-hover:text-slate-300 transition-colors select-none leading-relaxed">
                    Receive updates and offers <span className="text-slate-500">(Optional)</span>
                  </span>
                </label>
              )}
            </div>

            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-900/20 border border-red-900/50 rounded-lg text-red-400 text-sm mb-6">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleAuth} className="space-y-4">
              <div className="absolute -left-[9999px] top-0 h-0 w-0 overflow-hidden" aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input type="text" id="website" name="website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
              </div>
              <div className="space-y-2">
                <label htmlFor="email" className="block text-sm font-medium text-slate-300">
                  Email Address
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500 group-focus-within:text-orange-500 transition-colors">
                    <Mail size={18} />
                  </div>
                  <input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="block w-full pl-10 pr-3 py-3 bg-black border border-slate-700 rounded-xl text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 transition-all"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-3.5 px-4 bg-orange-600 hover:bg-orange-500 text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-orange-900/30 disabled:opacity-50 disabled:cursor-not-allowed group"
              >
                {loading ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <>
                    {mode === 'signup' ? 'Create Free Account' : 'Sign In'} 
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-slate-800 text-center">
              <p className="text-slate-400 text-sm">
                {mode === 'signup' ? 'Already have an account?' : "Don't have an account?"}
              </p>
              <button 
                onClick={() => { setMode(mode === 'login' ? 'signup' : 'login'); setError(null); }}
                className="text-orange-400 hover:text-orange-300 font-medium text-sm mt-1 transition-colors hover:underline"
              >
                {mode === 'signup' ? 'Log in here' : 'Sign up for free'}
              </button>
            </div>
          </div>

          {/* Right: Features + Pricing */}
          <div className="space-y-6">
            <div className="text-center md:text-left">
              <h2 className="text-2xl font-bold text-white mb-2">What you get</h2>
              <ul className="space-y-2 text-sm text-slate-400 max-h-[280px] overflow-y-auto pr-1">
                {PHOTOCLEAR_FEATURES.map((f, i) => (
                  <li key={i} className="flex gap-2">
                    <Sparkles size={14} className="shrink-0 mt-0.5 text-orange-400/80" />
                    <span><strong className="text-slate-300">{f.title}:</strong> {f.description}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="text-center md:text-left pt-2 border-t border-slate-800">
              <h2 className="text-2xl font-bold text-white mb-2">Transparent Pricing</h2>
              <p className="text-slate-400">{PHOTO_CLEAR_CREDIT_COST} credit per declutter</p>
            </div>

            {/* Billing Toggle */}
            <div className="flex bg-slate-900 rounded-xl p-1 border border-slate-800">
              <button
                onClick={() => setBillingTab('subscription')}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                  billingTab === 'subscription' 
                    ? 'bg-orange-600 text-white shadow-lg' 
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <Package size={14} className="inline mr-2" />
                Monthly Plans
              </button>
              <button
                onClick={() => setBillingTab('topup')}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                  billingTab === 'topup' 
                    ? 'bg-orange-600 text-white shadow-lg' 
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <Zap size={14} className="inline mr-2" />
                One-Time
              </button>
            </div>

            {/* Pricing Cards */}
            <div className="space-y-3">
              {getDisplayProducts().map((product) => (
                <div 
                  key={product.id}
                  className={`relative bg-slate-900/80 backdrop-blur border rounded-2xl p-5 transition-all hover:border-orange-500/50 ${
                    product.popular 
                      ? 'border-orange-500/50 shadow-lg shadow-orange-500/10' 
                      : 'border-slate-800'
                  }`}
                >
                  {product.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-gradient-to-r from-orange-500 to-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                        MOST POPULAR
                      </span>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-white">{product.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-2xl font-bold text-orange-400">{product.credits}</span>
                        <span className="text-slate-400 text-sm">credits{billingTab === 'subscription' ? '/month' : ''}</span>
                      </div>
                      <p className="text-slate-500 text-xs mt-1">{product.costPerCredit}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-white">
                        {currencySymbol}{product.displayAmount}
                      </div>
                      {billingTab === 'subscription' && (
                        <span className="text-slate-500 text-xs">/month</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-r from-slate-800/50 to-slate-900/50 rounded-xl p-4 border border-slate-700 text-center">
              <p className="text-slate-300 text-sm">
                <Sparkles size={14} className="inline text-orange-400 mr-1" />
                Create your free account to get started with <strong className="text-orange-400">10 free credits</strong>
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        {!isEmbed && (
          <div className="text-center mt-10 text-slate-500 text-sm">
            <p>© {new Date().getFullYear()} Photoclear.ai • Real Estate Photo Enhancement</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmbedPage;
